import React, { useCallback, useEffect, useMemo, useState } from "react";
import useStore from "../../../useStore";
import { useAuthentication } from "../../../Hooks/useAuthentication";
import Loader from "../../Elements/Loader/Loader";
import { useToast } from "../../../Contexts/Toast/toast.provider";
import "./getForms.css";
import Calendar from "../../Layout/Calendar/Calendar";
import BuscadorForms from "../../Layout/Buscador/BuscadorYFiltro";
import Button from "../../Elements/Button/Button";
import Modal from "../../Elements/Modal";
import {
  canViewForms as canViewFormsRole,
  canDeleteForms as canDeleteFormsRole,
  canEditFirma as canEditFirmaRole,
} from "../../../Constants/roles";

// ✅ el backend (getForms.mjs) ya excluye "OTRO" de los totales y ya filtra
// por fecha cuando le mandamos `date`; aquí solo se replica para poder
// recalcular el total del día cuando el usuario está buscando (ver
// `totalDia` más abajo).
const isProcesoOtro = (f) => {
  const p = String(f?.proceso || "").trim().toUpperCase();
  const po = String(f?.procesoOtro || "").trim();
  return p === "OTRO" || po.length > 0;
};

const dateToISO = (date) => {
  if (!date) return "";
  const yyyy = date.getFullYear();
  const mm = String(date.getMonth() + 1).padStart(2, "0");
  const dd = String(date.getDate()).padStart(2, "0");
  return `${yyyy}-${mm}-${dd}`;
};

const startOfToday = () => {
  const d = new Date();
  d.setHours(0, 0, 0, 0);
  return d;
};

const toInt = (v) => {
  const n = Number(v);
  return Number.isFinite(n) ? n : 0;
};

const tipoFolioText = (tipo) => {
  switch (String(tipo || "").trim().toUpperCase()) {
    case "SIMPLE":
      return "folios simples";
    case "DOBLE":
      return "folios dobles";
    case "TRIPLE":
      return "folios triples";
    default:
      return "folios";
  }
};

const GetForms = () => {
  const showToast = useToast();
  const { isAuthenticated, role } = useAuthentication();
  const {
    fetchDataGetForms,
    fetchDataDeleteForm,
    fetchDataUpdateForm,
    fetchDataGetUsers,
    usersList,
    forms,
    formsTotals,
    formsDatesWithForms,
    formsLoading: loading,
  } = useStore();

  const [search, setSearch] = useState("");
  const [results, setResults] = useState([]);
  const [deletingId, setDeletingId] = useState(null);
  const [formToDelete, setFormToDelete] = useState(null);
  const [formToEdit, setFormToEdit] = useState(null);
  const [editSelectedUser, setEditSelectedUser] = useState("");

  const canViewForms = canViewFormsRole(role);
  const canDeleteForms = canDeleteFormsRole(role);
  const canEditFirma = canEditFirmaRole(role);

  const [selectedDateObj, setSelectedDateObj] = useState(startOfToday());
  const selectedDate = useMemo(() => dateToISO(selectedDateObj), [selectedDateObj]);

  // ✅ solo pide el día seleccionado (el backend filtra y ya no manda la
  // tabla completa) — se vuelve a llamar cada vez que cambia `selectedDate`.
  const load = useCallback(async () => {
    try {
      await fetchDataGetForms(selectedDate);
    } catch (e) {
      const msg = e instanceof Error ? e.message : "Error al cargar formularios";
      showToast("error", msg);
    }
  }, [fetchDataGetForms, selectedDate, showToast]);

  const confirmDelete = useCallback(
    async () => {
      if (!formToDelete) return;
      const formId = formToDelete;

      setDeletingId(formId);
      setFormToDelete(null);
      try {
        await fetchDataDeleteForm(formId);
        showToast("success", "Formato eliminado");
        // ✅ refresca para que totales de semana/mes y el punto del
        // calendario (que ya no se recalculan solos en el cliente) queden
        // exactos.
        load();
      } catch (e) {
        const msg = e instanceof Error ? e.message : "Error al eliminar el formato";
        showToast("error", msg);
      } finally {
        setDeletingId(null);
      }
    },
    [formToDelete, fetchDataDeleteForm, showToast, load]
  );

  useEffect(() => {
    if (isAuthenticated && canViewForms) load();
  }, [isAuthenticated, canViewForms, load]);

  useEffect(() => {
    if (canEditFirma) fetchDataGetUsers().catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [canEditFirma]);

  // ✅ `forms` ya viene filtrado por día desde el backend (fetchDataGetForms
  // se llama con `selectedDate`), así que ya no hay que filtrarlo aquí.
  const dayItems = useMemo(() => (Array.isArray(forms) ? forms : []), [forms]);

  const listToShow = useMemo(() => {
    if (!search.trim()) return dayItems;
    const idsDelDia = new Set(dayItems.map((f) => f.FormId));
    return (Array.isArray(results) ? results : []).filter((f) => idsDelDia.has(f.FormId));
  }, [search, results, dayItems]);

  // Responde a la búsqueda (se recalcula sobre lo que se está mostrando).
  const totalDia = useMemo(() => {
    return listToShow.reduce((acc, f) => {
      if (isProcesoOtro(f)) return acc; // 👈 NO contar OTRO
      return acc + toInt(f?.totalEntregadas);
    }, 0);
  }, [listToShow]);

  // ✅ semana/mes ya vienen calculados del backend (no dependen de la
  // búsqueda, porque el frontend ya no tiene visibilidad de otros días).
  const totalSemana = formsTotals?.semana ?? 0;
  const totalMes = formsTotals?.mes ?? 0;

  if (!isAuthenticated) {
    return (
      <div className="container py-4">
        <div className="alert alert-warning mb-0">No has iniciado sesión.</div>
      </div>
    );
  }

  if (!canViewForms) {
    return (
      <div className="container py-4">
        <div className="alert alert-danger mb-0">No tienes permisos para ver formularios.</div>
      </div>
    );
  }

  if (loading) return <Loader />;

  return (
    <div className="gf-page">
      <div className="container py-4">
        {/* Header */}
        <div className="gf-header">
          <div>
            <h2 className="gf-title">Formularios</h2>
            <div className="gf-subtitle">
              Selecciona una fecha en el calendario y te muestro los formularios de ese día.
            </div>
          </div>

          <button className="btn gf-refresh-btn" onClick={load}>
            Refrescar
          </button>
        </div>

        {/* ✅ Totales */}
        <div className="gf-totals">
          <div className="gf-total-card">
            <div className="gf-total-label">Total del día</div>
            <div className="gf-total-value">{totalDia}</div>
            <div className="gf-total-sub">{selectedDate}</div>
          </div>

          <div className="gf-total-card">
            <div className="gf-total-label">Total semana (L-D)</div>
            <div className="gf-total-value">{totalSemana}</div>
            <div className="gf-total-sub">Semana de la fecha seleccionada</div>
          </div>

          <div className="gf-total-card">
            <div className="gf-total-label">Total del mes</div>
            <div className="gf-total-value">{totalMes}</div>
            <div className="gf-total-sub">Mes de la fecha seleccionada</div>
          </div>
        </div>

        {/* ✅ Layout sticky + scroll */}
        <div className="row g-3 gf-layout">
          {/* Sidebar sticky (Buscador + Calendario) */}
          <div className="col-12 col-lg-4">
            <div className="gf-sticky">
              <div className="mb-3">
                <BuscadorForms
                  search={search}
                  setSearch={setSearch}
                  data={forms}
                  onResults={setResults}
                />
              </div>

              <div className="gf-card">
                <div className="gf-card-body">
                  <div className="gf-section-title">Calendario</div>

                  <Calendar
                    selectedDateObj={selectedDateObj}
                    onChangeDate={setSelectedDateObj}
                    datesWithForms={formsDatesWithForms}
                  />

                  <div className="gf-selected mt-3">
                    Mostrando: <span className="gf-selected-date">{selectedDate}</span>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Panel con scroll (solo data) */}
          <div className="col-12 col-lg-8">
            <div className="gf-scroll-panel">
              {dayItems.length === 0 ? (
                <div className="alert alert-info gf-alert">
                  No hay formularios para <b>{selectedDate}</b>.
                </div>
              ) : listToShow.length === 0 ? (
                <div className="alert alert-warning gf-alert">
                  No hay resultados para tu búsqueda en <b>{selectedDate}</b>.
                </div>
              ) : (
                <div className="row g-3">
                  {listToShow.map((f) => (
                    <div className="col-12" key={f.FormId}>
                      <div className="gf-form-card">
                        {/* Top */}
                        <div className="gf-form-head">
                          <div className="gf-form-title">
                            OT: <b>{f.noOT || "-"}</b> · Máquina: <b>{f.maquina || "-"}</b> · Cliente:{" "}
                            <b>{f.cliente || "-"}</b>
                          </div>

                          <div className="gf-badges">
                            <span className="badge gf-badge-dark">Turno: {f.turno || "-"}</span>
                            <span className="badge gf-badge-soft">
                              Proceso:{" "}
                              {f.proceso === "OTRO" && f.procesoOtro
                                ? `OTRO - ${f.procesoOtro}`
                                : f.proceso || "-"}
                            </span>
                          </div>

                          {canEditFirma && (
                            <Button
                              text="Editar firma"
                              color="blue"
                              fill
                              padding
                              classNames="gf-delete-btn"
                              action={() => {
                                setFormToEdit(f);
                                setEditSelectedUser(f.submittedBy?.username || "");
                              }}
                            />
                          )}

                          {canDeleteForms && (
                            <Button
                              text={deletingId === f.FormId ? "Eliminando..." : "Eliminar"}
                              color="red"
                              fill
                              padding
                              classNames="gf-delete-btn"
                              disabled={deletingId === f.FormId}
                              action={() => setFormToDelete(f.FormId)}
                            />
                          )}
                        </div>

                        <div className="gf-divider" />

                        {/* Campos seccionados */}
                        <div className="gf-sections">
                          <div className="gf-section">
                            <div className="gf-section-h">Datos generales</div>
                            <div className="gf-grid">
                              <div className="gf-item">
                                <div className="gf-muted">Fecha</div>
                                <div className="gf-strong">
                                  {f.fecha || (f.createdAt ? String(f.createdAt).slice(0, 10) : "-")}
                                </div>
                              </div>

                              <div className="gf-item">
                                <div className="gf-muted">Folios del</div>
                                <div className="gf-strong">{f.foliosDel || "-"}</div>
                              </div>

                              <div className="gf-item">
                                <div className="gf-muted">Al</div>
                                <div className="gf-strong">{f.al || "-"}</div>
                              </div>

                              <div className="gf-item">
                                <div className="gf-muted">Logotipo</div>
                                <div className="gf-strong">{f.logotipo || "-"}</div>
                              </div>

                              <div className="gf-item">
                                <div className="gf-muted">Modelo</div>
                                <div className="gf-strong">{f.modelo || "-"}</div>
                              </div>

                              <div className="gf-item">
                                <div className="gf-muted">Longitud</div>
                                <div className="gf-strong">{f.longitud || "-"}</div>
                              </div>

                              <div className="gf-item">
                                <div className="gf-muted">Color</div>
                                <div className="gf-strong">{f.color || "-"}</div>
                              </div>
                            </div>
                          </div>

                          <div className="gf-section">
                            <div className="gf-section-h">Horas</div>
                            <div className="gf-grid">
                              <div className="gf-item">
                                <div className="gf-muted">Hora inicio</div>
                                <div className="gf-strong">{f.horaInicio || "-"}</div>
                              </div>

                              <div className="gf-item">
                                <div className="gf-muted">Hora término</div>
                                <div className="gf-strong">{f.horaTermino || "-"}</div>
                              </div>

                              <div className="gf-item">
                                <div className="gf-muted">Registro Creado</div>
                                <div className="gf-strong">
                                  {f.createdAt ? new Date(f.createdAt).toLocaleString() : "-"}
                                </div>
                              </div>
                            </div>
                          </div>

                          <div className="gf-section">
                            <div className="gf-section-h">Entregas</div>
                            <div className="gf-grid">
                              <div className="gf-item">
                                <div className="gf-muted">
                                  Total entregadas ({tipoFolioText(f.tipoFolio)})
                                </div>
                                <div className="gf-strong">{f.totalEntregadas ?? "-"}</div>
                              </div>
                            </div>
                          </div>

                          <div className="gf-section">
                            <div className="gf-section-h">Mermas</div>
                            <div className="gf-grid">
                              <div className="gf-item">
                                <div className="gf-muted">Mermas defecto</div>
                                <div className="gf-strong">{f.mermasDefecto ?? "-"}</div>
                              </div>

                              <div className="gf-item">
                                <div className="gf-muted">Mermas error humano</div>
                                <div className="gf-strong">{f.mermasErrorHumano ?? "-"}</div>
                              </div>

                              <div className="gf-item">
                                <div className="gf-muted">Mermas recupera</div>
                                <div className="gf-strong">{f.mermasRecupera ?? "-"}</div>
                              </div>
                            </div>
                          </div>

                          <div className="gf-section">
                            <div className="gf-section-h">Pruebas</div>
                            <div className="gf-grid">
                              <div className="gf-item">
                                <div className="gf-muted">Pruebas calidad</div>
                                <div className="gf-strong">{f.pruebasCalidad ?? "-"}</div>
                              </div>
                            </div>
                          </div>

                          <div className="gf-section">
                            <div className="gf-section-h">Observaciones</div>
                            <div className="gf-grid">
                              <div className="gf-item gf-item-wide">
                                <div className="gf-muted">Observaciones</div>
                                <div className="gf-strong">{f.observaciones || "-"}</div>
                              </div>
                            </div>
                          </div>
                        </div>

                        {f.submittedBy?.username && (
                          <div className="gf-signed">
                            Firmado por: <b className="text-dark">{f.submittedBy.username}</b>
                            {f.submittedBy.role ? ` (${f.submittedBy.role})` : ""}
                          </div>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      <Modal
        display={!!formToDelete}
        onClose={() => setFormToDelete(null)}
        title="Eliminar formato"
        size="small"
      >
        <p className="gf-delete-confirm-text">
          ¿Seguro que quieres eliminar este formato? Esta acción no se puede deshacer.
        </p>
        <div className="gf-delete-confirm-actions">
          <Button text="Cancelar" color="gray" action={() => setFormToDelete(null)} />
          <Button text="Eliminar" color="red" fill action={confirmDelete} />
        </div>
      </Modal>

      <Modal
        display={!!formToEdit}
        onClose={() => setFormToEdit(null)}
        title="Editar firma operador"
        size="small"
      >
        <p className="gf-delete-confirm-text">
          Selecciona el operador que realizó este formulario:
        </p>
        <select
          className="form-select mb-3"
          value={editSelectedUser}
          onChange={(e) => setEditSelectedUser(e.target.value)}
        >
          <option value="">— Selecciona operador —</option>
          {usersList.map((u) => (
            <option key={u.userId || u.username} value={u.username}>
              {u.username}
            </option>
          ))}
        </select>
        <div className="gf-delete-confirm-actions">
          <Button text="Cancelar" color="gray" action={() => setFormToEdit(null)} />
          <Button
            text="Guardar"
            color="green"
            fill
            disabled={!editSelectedUser}
            action={async () => {
              const u = usersList.find((x) => x.username === editSelectedUser);
              try {
                await fetchDataUpdateForm(formToEdit.FormId, {
                  userId: u?.userId || null,
                  username: editSelectedUser,
                  role: u?.role || null,
                });
                showToast("success", "Firma actualizada");
                setFormToEdit(null);
              } catch (e) {
                showToast("error", e instanceof Error ? e.message : "Error al actualizar");
              }
            }}
          />
        </div>
      </Modal>
    </div>
  );
};

export default GetForms;